// src/components/product/ExpirationSection.js
import React from "react";
import { Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { Input } from "../ui";

export function ExpirationSection({
  category,
  categoryLabel,
  productState,
  expiryDate,
  storageCondition,
  onExpiryDateChange,
  onCollectionDateChange,
  onBestBeforeChange,
  minDate,
  collectionDate,
  bestBefore,
  isAnimalCategory = false,
  animalConfig = null,
  labels = {}
}) {
  const isLiveAnimal = animalConfig?.productType === "live_animal";
  const allowsCollection = animalConfig?.allowsCollectionDate;
  const allowsBestBefore = animalConfig?.allowsBestBefore;
  const expiryRequired = animalConfig ? animalConfig.requiresExpiry : true;

  return (
    <div className="bg-white dark:bg-neutral-800/80 rounded-xl p-4 sm:p-6 shadow-md dark:shadow-lg border border-gray-200 dark:border-neutral-700/50 space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Clock className="w-5 h-5 text-green-600" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {labels.sectionTitle || "Expiration and Storage"}
        </h2>
      </div>

      {/* Live animal hint — no food expiry required */}
      {isLiveAnimal && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              {labels.liveAnimalHint || "Live animals do not require a food expiry date."}
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              {labels.liveAnimalSubhint || "You may still record a seller-provided availability note in the description."}
            </p>
          </div>
        </div>
      )}

      {/* Expiry date — optional for animals, required for crops */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {labels.expiryDateLabel || "Expiry Date"}
          {!expiryRequired && <span className="text-gray-400 dark:text-gray-500 font-normal"> (optional)</span>}
        </label>
        <Input
          type="date"
          name="expiryDate"
          value={expiryDate}
          onChange={onExpiryDateChange}
          required={expiryRequired}
          min={minDate}
        />
        {category && productState && expiryDate && (
          <p className="mt-2 text-xs text-green-700 dark:text-green-400 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            {labels.suggestedBasedOn || "Suggested based on"} {categoryLabel || category} ({productState})
          </p>
        )}
      </div>

      {/* Egg collection date + best before */}
      {allowsCollection && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {labels.collectionDateLabel || "Collection date"}
              <span className="text-gray-400 dark:text-gray-500 font-normal"> (optional)</span>
            </label>
            <Input
              type="date"
              name="collectionDate"
              value={collectionDate || ""}
              onChange={onCollectionDateChange}
              min={minDate}
            />
            {labels.collectionDateHint && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{labels.collectionDateHint}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {labels.bestBeforeLabel || "Best before"}
              <span className="text-gray-400 dark:text-gray-500 font-normal"> (optional)</span>
            </label>
            <Input
              type="date"
              name="bestBefore"
              value={bestBefore || ""}
              onChange={onBestBeforeChange}
              min={minDate}
            />
            {labels.bestBeforeHint && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{labels.bestBeforeHint}</p>
            )}
          </div>
        </div>
      )}

      {/* Smart info box — crops only */}
      {category && productState && !isAnimalCategory && (
        <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg">
          <p className="text-xs sm:text-sm text-blue-900 dark:text-blue-100">
            <span className="font-semibold text-blue-700 dark:text-blue-300">{labels.smartTitle || "Smart Auto-Management:"}</span> {labels.smartDescription || "Based on your selections, this product has been configured for optimal shelf life."} {labels.productStateText || "Product state"} <span className="font-medium">{productState}</span>, {labels.storageText || "Storage"}: <span className="font-medium">{storageCondition}</span>.
          </p>
        </div>
      )}
    </div>
  );
}